import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import {
    UiSelect,
    UiText,
} from '@experiences/ui-common';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import React, { useMemo } from 'react';
import {
    Controller,
    useFormContext,
} from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
    certificateLocationUsageOptions,
    Saml2BindingType,
    samlBindingTypeOptions,
} from '../../../common/constants/ExternalIdentityProviderConstant';
import type { ISamlFormData } from '../../../common/interfaces/cis/saml';

const useStyles = makeStyles(() =>
    createStyles({
        sectionTitle: {
            marginTop: '20px',
            marginBottom: '0px',
        },
        section: {
            display: 'flex',
            alignItems: 'flex-end',
        },
        field: {
            marginBottom: '16px',
            maxWidth: '420px',
        },
        formSection: {
            display: 'flex',
            flexDirection: 'column',
        },
        switchLabel: { lineHeight: '20px' },
    }),
);

const SecuritySettingsSAMLAdvancedDetailsForm: React.FC = () => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const {
        control, formState: { errors },
    } = useFormContext<ISamlFormData>();

    const HideSAMLServiceCertificateUsage = useFeatureFlagValue(Features.HideSAMLServiceCertificateUsage.name);

    const bindingTypes = useMemo(() => {
        const options: any = { ...samlBindingTypeOptions };
        delete options[Saml2BindingType.Artifact];

        return options;
    }, []);

    return (
        <>
            <div className={classes.formSection}>
                <Controller
                    name="AllowUnsolicitedAuthnResponse"
                    control={control}
                    render={({ field }) => (
                        <FormControlLabel
                            className={classes.field}
                            control={
                                <Switch
                                    checked={field.value}
                                    onChange={e => field.onChange(e.target.checked)}
                                    color="primary"
                                    data-cy="configure-saml-allow-unsolicited-authn-response"
                                />
                            }
                            label={
                                <UiText className={classes.switchLabel}>
                                    {translate({ id: 'CLIENT_ALLOW_UNSOLICITED_AUTHENTICATION_RESPONSE' })}
                                </UiText>
                            }
                        />
                    )}
                />
                <UiSelect
                    control={control}
                    className={classes.field}
                    name="Saml2BindingType"
                    inputLabel={translate({ id: 'CLIENT_SAML_BINDING_TYPE' })}
                    error={!!errors.Saml2BindingType}
                    options={bindingTypes}
                    required
                    helperText={errors.Saml2BindingType?.type === 'required' && translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' })}
                    fullWidth
                    dataCy="configure-saml2-binding-type"
                />
                {!HideSAMLServiceCertificateUsage && <UiSelect
                    control={control}
                    className={classes.field}
                    name="ServiceCertificateUsage"
                    inputLabel={translate({ id: 'CLIENT_SERVICE_CERTIFICATE_USAGE' })}
                    error={!!errors.ServiceCertificateUsage}
                    options={certificateLocationUsageOptions}
                    fullWidth
                    dataCy="edit-certificate-locations-service-usage"
                    required
                    helperText={errors.ServiceCertificateUsage?.type === 'required' && translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' })}
                />}
            </div>
        </>
    );
};

export default SecuritySettingsSAMLAdvancedDetailsForm;
